import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
  getUserLinkPages,
  createLinkPage,
  deleteLinkPage,
  toggleLinkPageStatus,
  type CreateLinkPageInput,
} from '@/services/linkpages';
import type { LinkPage } from '@/types';

export const useLinkPages = () => {
  const { user } = useAuth();
  const [linkPages, setLinkPages] = useState<LinkPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLinkPages = useCallback(async () => {
    if (!user) {
      setLinkPages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getUserLinkPages(user.uid);
      setLinkPages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch link pages');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLinkPages();
  }, [fetchLinkPages]);

  const create = async (input: Omit<CreateLinkPageInput, 'userId'>) => {
    if (!user) throw new Error('User not authenticated');

    const newPage = await createLinkPage({ ...input, userId: user.uid });
    setLinkPages((prev) => [newPage, ...prev]);
    return newPage;
  };

  const remove = async (id: string) => {
    await deleteLinkPage(id);
    setLinkPages((prev) => prev.filter((p) => p.id !== id));
  };

  const toggleStatus = async (id: string) => {
    const updated = await toggleLinkPageStatus(id);
    setLinkPages((prev) => prev.map((p) => (p.id === id ? updated : p)));
    return updated;
  };

  return {
    linkPages,
    loading,
    error,
    create,
    remove,
    toggleStatus,
    refresh: fetchLinkPages,
  };
};
